import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import {
  ArrowRight,
  FileText,
  Download,
  BookOpen,
  Lightbulb,
  CheckCircle,
} from "lucide-react";

const ResourcesPage = () => {
  const leadMagnets = [
    {
      slug: "business-automation-guide",
      title: "Free Business Automation Guide",
      description: "A comprehensive guide to automating your business operations and marketing for maximum efficiency.",
      highlights: ["Automation fundamentals", "Tool recommendations", "Implementation roadmap", "ROI calculator"],
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600",
    },
    {
      slug: "ai-growth-blueprint",
      title: "AI-Assisted Growth Blueprint",
      description: "Strategic framework for leveraging AI to accelerate business growth without the hype.",
      highlights: ["AI use cases by function", "Implementation priorities", "Tool selection guide", "Success metrics"],
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600",
    },
    {
      slug: "lead-generation-checklist",
      title: "Lead Generation Systems Checklist",
      description: "Step-by-step checklist for building lead generation systems that deliver consistent results.",
      highlights: ["Channel evaluation", "Lead magnet ideas", "Capture optimization", "Qualification process"],
      image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600",
    },
    {
      slug: "marketing-automation-playbook",
      title: "Marketing Automation Playbook for SMEs",
      description: "Practical playbook for small and medium businesses ready to implement marketing automation.",
      highlights: ["Platform comparison", "Workflow templates", "Email sequences", "Scaling strategies"],
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600",
    },
  ];

  return (
    <div className="pt-32" data-testid="resources-page">
      <SEO 
        title="Free SEO & Marketing Resources"
        description="Download free guides on SEO strategy, marketing automation, lead generation checklists, and automation playbooks."
        keywords="free SEO guide, marketing automation guide, lead generation checklist, business automation resources, free digital marketing templates"
      />
      <Breadcrumb items={[{ name: "Resources" }]} />
      {/* Hero Section */}
      <section className="section pb-0" data-testid="resources-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="badge badge-blue mb-4">Resources</span>
              <h1 className="font-heading text-4xl lg:text-5xl font-bold text-optima-dark mb-6">
                Free SEO & Marketing Automation Resources
              </h1>
              <p className="text-lg text-slate-600">
                Actionable guides, templates, and frameworks to help you implement growth strategies in your business.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lead Magnets Grid */}
      <section className="section" data-testid="lead-magnets-grid">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            {leadMagnets.map((magnet, index) => (
              <motion.div
                key={magnet.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link
                  to={`/guide/${magnet.slug}`}
                  className="card block group overflow-hidden h-full"
                  data-testid={`resource-${magnet.slug}`}
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={magnet.image}
                      alt={`${magnet.title} - free SEO and marketing automation resource`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-optima-cyan text-sm font-medium mb-3">
                      <FileText className="w-4 h-4" />
                      Free Guide
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-optima-dark mb-2 group-hover:text-optima-blue transition-colors">
                      {magnet.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4">{magnet.description}</p>
                    <div className="space-y-2 mb-4">
                      {magnet.highlights.map((highlight) => (
                        <div key={highlight} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-sm text-slate-600">{highlight}</span>
                        </div>
                      ))}
                    </div>
                    <span className="inline-flex items-center gap-2 text-optima-blue font-medium">
                      Get Free Access <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="section-alt" data-testid="additional-resources">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="badge badge-cyan mb-4">More Resources</span>
            <h2 className="font-heading text-3xl font-bold text-optima-dark mb-6">
              Explore More Ways to Learn
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Link
                to="/blog"
                className="block bg-white rounded-xl p-6 shadow-soft hover:shadow-medium transition-shadow text-center"
                data-testid="resource-blog"
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-optima-blue/10 flex items-center justify-center">
                  <BookOpen className="w-7 h-7 text-optima-blue" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-optima-dark mb-2">
                  Blog Articles
                </h3>
                <p className="text-slate-600 text-sm">
                  In-depth articles on growth strategies, automation, and marketing.
                </p>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Link
                to="/case-studies"
                className="block bg-white rounded-xl p-6 shadow-soft hover:shadow-medium transition-shadow text-center"
                data-testid="resource-case-studies"
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-optima-cyan/10 flex items-center justify-center">
                  <Lightbulb className="w-7 h-7 text-optima-cyan" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-optima-dark mb-2">
                  Case Studies
                </h3>
                <p className="text-slate-600 text-sm">
                  Real examples of how we've helped businesses grow.
                </p>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Link
                to="/growth-systems"
                className="block bg-white rounded-xl p-6 shadow-soft hover:shadow-medium transition-shadow text-center"
                data-testid="resource-growth-systems"
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-optima-pink/10 flex items-center justify-center">
                  <Download className="w-7 h-7 text-optima-pink" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-optima-dark mb-2">
                  Growth Systems
                </h3>
                <p className="text-slate-600 text-sm">
                  Explore the systematic approaches we use to drive growth.
                </p>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-optima-dark" data-testid="resources-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-3xl font-bold text-white mb-6">
              Need Personalized Guidance?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Resources are great, but nothing beats a conversation. Book a free growth audit for tailored recommendations.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors"
              data-testid="resources-book-call"
            >
              Book a Free Growth Audit <ArrowRight className="w-5 h-5" />
            </Link>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <Link to="/services" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Explore Services</Link>
              <Link to="/pricing" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">View Pricing</Link>
              <Link to="/blog" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Read Our Blog</Link>
              <Link to="/client-onboarding" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Start Onboarding</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
